import { useEffect, useState } from 'react';
import { Monitor } from 'lucide-react';
import { Label } from '../ui/label';
import { API_BASE } from '../../utils/api-base';

// A client can have several bridge PCs (one per site / panel). Each device is read by
// the bridge it is assigned to; "Any bridge PC" (no assignment) means every bridge of
// the client tries it — fine with one PC, but with two PCs both open the same COM
// port / drive, and a PC without that port keeps reporting errors.

export type ClientBridge = { bridgeId: string; mac: string; isActive: boolean; lastSeenAt: string | null };

const cache = new Map<string, Promise<ClientBridge[]>>();

function loadBridges(token: string, clientId: string): Promise<ClientBridge[]> {
  const key = `${clientId}`;
  if (!cache.has(key)) {
    const p = fetch(`${API_BASE}/api/admin/clients/${encodeURIComponent(clientId)}/bridges`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : { bridges: [] }))
      .then((d) => (Array.isArray(d?.bridges) ? d.bridges : []).map((b: any) => ({
        bridgeId: String(b.bridge_id),
        mac: String(b.mac_address || ''),
        isActive: b.is_active !== false,
        lastSeenAt: b.last_seen_at || null,
      })))
      .catch(() => []);
    cache.set(key, p);
    // Refresh on the next open of a form (new PCs, last-seen times).
    p.finally(() => setTimeout(() => cache.delete(key), 30_000));
  }
  return cache.get(key)!;
}

export function useClientBridges(token: string | null | undefined, clientId: string | null | undefined) {
  const [bridges, setBridges] = useState<ClientBridge[]>([]);
  useEffect(() => {
    if (!token || !clientId) { setBridges([]); return; }
    let alive = true;
    loadBridges(token, clientId).then((b) => { if (alive) setBridges(b); });
    return () => { alive = false; };
  }, [token, clientId]);
  return bridges;
}

const seen = (iso: string | null) => {
  if (!iso) return 'never seen';
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 90) return 'online now';
  if (s < 3600) return `seen ${Math.round(s / 60)} min ago`;
  if (s < 86400) return `seen ${Math.round(s / 3600)} h ago`;
  return `seen ${Math.round(s / 86400)} d ago`;
};

/** Short name for a device card: "PC 04:BF:1B:09:5E:2E", or null when not assigned. */
export function bridgeName(bridges: ClientBridge[], bridgeId?: string | null) {
  if (!bridgeId) return null;
  const b = bridges.find((x) => x.bridgeId === bridgeId);
  return b ? `PC ${b.mac || b.bridgeId}` : 'PC (removed bridge)';
}

export function BridgePicker({ bridges, value, onChange, id = 'bridge-pc' }: {
  bridges: ClientBridge[]; value: string; onChange: (bridgeId: string) => void; id?: string;
}) {
  const known = !value || bridges.some((b) => b.bridgeId === value);
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="flex items-center gap-1.5"><Monitor className="h-3.5 w-3.5" /> Bridge PC</Label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="flex h-9 w-full rounded-md border border-input bg-input-background px-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
      >
        <option value="">Any bridge PC of this client</option>
        {bridges.map((b) => (
          <option key={b.bridgeId} value={b.bridgeId}>
            PC {b.mac || b.bridgeId} · {b.isActive ? seen(b.lastSeenAt) : 'disabled'}
          </option>
        ))}
        {!known && <option value={value}>Removed bridge ({value})</option>}
      </select>
      <p className="text-xs text-muted-foreground">
        {bridges.length > 1
          ? 'This client has several bridge PCs. Pick the PC this device is wired to: only that PC opens its COM port / network link, the others leave it alone.'
          : 'Only one bridge PC so far. Pick it now so a second PC added later does not touch this device.'}
      </p>
    </div>
  );
}
